import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchEventBySlug, fetchArtistBySlug, fetchEvents } from '../api/api';
import ArtistModalContent from '../components/ArtistModalContent';
import Loader from '../components/Loader';
import SEO from '../components/SEO';
import HomeEventList from '../components/HomeEventList';
import styles from '../styles/pages/_EventPage.module.scss';
import { useCustomNavigation } from '../components/NavigationContext';
import Billetterie from '../components/Billetterie';

const EventPage = () => {
    const { customNavigate } = useCustomNavigation();
    const { slug } = useParams();
    const [eventData, setEventData] = useState(null);
    const [artistData, setArtistData] = useState(null);
    const [weekEvents, setWeekEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isContentReady, setIsContentReady] = useState(false);

    const decodeHtmlEntities = (text) => {
        if (!text) return '';
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
    };

    useEffect(() => {
        const loadEventData = async () => {
            if (!slug) return;

            setIsLoading(true);
            setError(null);
            setIsContentReady(false);

            try {
                const currentEvent = await fetchEventBySlug(slug);
                if (!currentEvent) throw new Error('Événement non trouvé');

                const supportArtists = currentEvent.artistesSupport || [];
                setEventData({
                    ...currentEvent,
                    supportArtists,
                });

                if (currentEvent.artistePrincipal?.slug) {
                    const artist = await fetchArtistBySlug(currentEvent.artistePrincipal.slug);
                    setArtistData(artist);
                }

                const allEvents = await fetchEvents();
                const eventDate = new Date(currentEvent.date);

                const weekStart = new Date(eventDate.setDate(eventDate.getDate() - eventDate.getDay() + 1));
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekEnd.getDate() + 6);

                const filteredWeekEvents = allEvents.filter((e) => {
                    const date = new Date(e.date);
                    return date >= weekStart && date <= weekEnd && e.id !== currentEvent.id;
                });

                setWeekEvents(filteredWeekEvents);
                setIsContentReady(true);
            } catch (error) {
                console.error('Erreur lors du chargement des données:', error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        loadEventData();
    }, [slug]);

    const renderContent = () => {
        if (isLoading || !isContentReady) {
            return (
                <div className={styles.loadingContainer}>
                    <Loader type="rings" />
                </div>
            );
        }

        if (error) return <div className={styles.error}>{error}</div>;
        if (!eventData) return <div className={styles.error}>Événement non trouvé</div>;

        const formattedDate = eventData.date
            ? new Date(eventData.date).toLocaleDateString('fr-FR')
            : 'Date inconnue';
        
        const eventTitle = typeof eventData.title === 'object' ? eventData.title.rendered : eventData.title;
        const decodedTitle = decodeHtmlEntities(eventTitle);
        const seoTitle = `${decodedTitle} - ${formattedDate}`;
        const seoDescription = eventData.description || `Découvrez ${decodedTitle} le ${formattedDate} au Poney Club Toulouse 2025`;

        return (
            <>
                <SEO
                    title={seoTitle}
                    description={seoDescription}
                    image={artistData?.acf?.photo?.[0]?.url || '/poneyseo.png'}
                    url={`programmation/${slug}`}
                    type="event"
                />
                <div className={styles.eventPage}>
                    {artistData && eventData && (
                        <ArtistModalContent
                            selectedData={{
                                artist: artistData,
                                event: {
                                    ...eventData,
                                    title: decodedTitle
                                },
                                supportArtists: eventData.artistesSupport,
                            }}
                            onClose={() => customNavigate('/programmation')}
                            onTicketClick={() => customNavigate('/billetterie')}
                        />
                    )}

                    {weekEvents.length > 0 && (
                        <HomeEventList 
                            events={weekEvents} 
                            currentEventId={eventData.id} 
                            isEventPage={true} 
                            className="vertical-list"
                        />
                    )}

                    <Billetterie/>
                </div>
            </>
        );
    };

    return (
        <div className={`${styles.eventPageContainer} ${isLoading || !isContentReady ? styles.loading : ''}`}>
            {renderContent()}
        </div>
    );
};

export default EventPage;
